import { FC, useEffect, useState } from 'react';
import BrokerListView from './BrokerListView';
import _ from 'lodash';
import PortalToTarget from 'v3/common/components/PortalToTarget';
import { useResponsive } from 'v3/common/hooks/useResponsive';
import { useLoginContext } from '../LoginProvider';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

//  TODO Cleanups
const BrokersList: FC = () => {
  const { state, onPartialStateUpdate, handleLogin, brokersToShowInUi } = useLoginContext();

  const {
    searchValue,
    filteredBrokerLists = [],
    previouslyLoggedInBrokerList = [],
    selectedBrokerConfig,
  } = state;

  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const shouldShowPreviouslyUsedBrokers = _.size(previouslyLoggedInBrokerList) >= 1 && !searchValue;

  const isMobile = useResponsive();

  const firstFiltered = filteredBrokerLists && filteredBrokerLists.length > 0 ? filteredBrokerLists[0] : null;
  const firstPrev =
    previouslyLoggedInBrokerList && previouslyLoggedInBrokerList.length > 0
      ? previouslyLoggedInBrokerList[0]
      : null;
  
  const isCrypto = ApplicationDomainUtility.isCrypto;

  const topBrokerNames = filteredBrokerLists.slice(0, 2).map(({ name }) => name).join(', ');

  const headerComponent = (
    <div className="broker-list__header">
      <i className="fa-regular fa-magnifying-glass" />
      <input
        type="text"
        name="broker-search"
        placeholder={ isCrypto ? `Search brokers like ${topBrokerNames }...` : "Search brokers like Fyers, Dhan..."}
        autoFocus={!isMobile}
        value={searchValue}
        onChange={(e) => {
          // This will optimize the effect in the hooks, with single render count
          const filteredBrokers = _.filter(brokersToShowInUi, (l) =>
            _.includes(_.toLower(l.name), _.toLower(_.trim(e.target.value)))
          );

          onPartialStateUpdate({
            filteredBrokerLists: filteredBrokers,
            selectedBrokerConfig: null,
            searchValue: e.target.value,
          });
        }}
        autoComplete="off"
        onKeyDown={(e) => {
          const currentList = filteredBrokerLists;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!currentList || currentList.length === 0) return;
            setHighlightedIndex((prev) => Math.min(prev + 1, currentList.length - 1));
            return;
          }

          if (e.key === 'ArrowUp') {
            e.preventDefault();

            if (!currentList || currentList.length === 0) return;
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            return;
          }

          if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0) {
              const broker = currentList && currentList[highlightedIndex];
              if (broker) {
                handleLogin(broker);
              }
              return;
            }

            if (selectedBrokerConfig) {
              handleLogin(selectedBrokerConfig);
              return;
            }

            if (!_.isEmpty(searchValue) && firstFiltered) {
              handleLogin(firstFiltered);
              return;
            }

            const fallback = firstPrev || firstFiltered;
            if (fallback) {
              handleLogin(fallback);
            }
            return;
          }
        }}
      />
    </div>
  );

  const bodyComponent = (
    <div className="broker-list__body">
      {shouldShowPreviouslyUsedBrokers && (
        <div className="previously-selected-broker-list">
          <b className="title">Previously Used Brokers</b>
          {_.map(previouslyLoggedInBrokerList, (list) => (
            <BrokerListView key={list.id} list={list} />
          ))}
        </div>
      )}

      <div className="brokers-list">
        <b className="title">Browse All Brokers</b>
        <div className="list">
          {_.map(filteredBrokerLists, (list, index) => (
            <BrokerListView
              key={list.id}
              list={list}
              isHovered={
                index === highlightedIndex ||
                (selectedBrokerConfig && selectedBrokerConfig.id === list.id) ||
                false
              }
            />
          ))}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const body = document.querySelector('.login-modal__body') as HTMLElement | null;
    const fader = document.querySelector('.broker-list__fadder') as HTMLElement | null;

    if (!body || !fader) return;

    const updateFader = () => {
      const isAtBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1;

      fader.classList.toggle('hide', isAtBottom);
    };

    body.addEventListener('scroll', updateFader);

    updateFader();
    setHighlightedIndex(-1);
    return () => body.removeEventListener('scroll', updateFader);
  }, [searchValue]);

  const handleClickDownArrow = () => {
    const body = document.querySelector('.login-modal__body') as HTMLElement | null;
    if (!body) return;

    body.scrollTo({
      top: body.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div className="broker-list">
      <div className="broker-list__fadder">
        <div className="scroll-down-arrow" onClick={handleClickDownArrow}>
          <i className="fa-sharp fa-solid fa-angle-down"></i>
        </div>
      </div>
      <PortalToTarget targetClassName="login-modal__header" allowForWeb>
        {headerComponent}
      </PortalToTarget>
      {bodyComponent}
    </div>
  );
};

export default BrokersList;
